
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

@Component
export default class FeaturesSection extends Vue {
  @Prop({ default: 0 }) readonly currentSectionIndex!: number;
  @Prop({ default: null }) readonly dissolvingNextCtaIndex!: number | null;
  public seoFeatures = [
    {
      title: 'RAWS, dwa, trzy!',
      icon: 'mdi-image-multiple',
      description: 'Wszystko masz pod ręką. Pierwszą sesję przygotujesz w kilka minut, a obsługę RAWS opanujesz przy okazji.',
    },
    {
      title: 'Nie ma RAWS bez RAW.',
      icon: 'mdi-file-image-outline',
      description: 'Dodaj RAW-y prosto z aparatu lub Lightrooma. Zamienimy je na JPG na Twoim urządzeniu. Oryginały zostaną u Ciebie.',
    },
    {
      title: 'Złap, przenieś, przeciągnij.',
      icon: 'mdi-drag',
      description: 'Upuść pliki na kafelku sesji i korzystaj ze skrótów, które już znasz. Rozsiądź się wygodnie.',
    },
    {
      title: 'Porządek w folderach.',
      icon: 'mdi-folder-multiple-outline',
      description: 'Układaj sesje według klientów, terminów lub własnego pomysłu. Gdy klient wróci po zdjęcia, od razu trafisz do jego galerii.',
    },
    {
      title: 'Twoja galeria, Twoje zasady.',
      icon: 'mdi-tune',
      description: 'Cennik, znak wodny, wiadomości, chat i pobieranie ustawiasz w jednym miejscu. Kilka kliknięć i galeria jest gotowa.',
    },
    {
      title: 'Lightroom i RAWS grają razem.',
      icon: 'mdi-image-edit-outline',
      description: 'Obrabiaj zdjęcia w Lightroomie i dodawaj je prosto do galerii RAWS. Twój rytm pracy zostaje taki, jak lubisz.',
    },
    {
      title: 'Jedna sesja. Jeden link.',
      icon: 'mdi-account-check-outline',
      description: 'Wyślij klientowi galerię jednym linkiem. Wybierze ujęcia, złoży zamówienie, a link do gotowych plików dostanie e-mailem.',
    },
    {
      title: 'Skrót do wygody.',
      icon: 'mdi-keyboard',
      description: 'Ctrl/Cmd+A zaznacza wszystkie zdjęcia, Escape czyści wybór. Przy dużej sesji takie drobiazgi oszczędzają Ci czas.',
    },
  ];
  get section5LoopFeatures(): Array<{ key: string; title: string; icon: string; description: string }> {
    const loopCopies = 3;
    const features: Array<{ key: string; title: string; icon: string; description: string }> = [];
    for (let copyIndex = 0; copyIndex < loopCopies; copyIndex += 1) {
      this.seoFeatures.forEach((feature, featureIndex) => {
        features.push({
          key: `section5-feature-${copyIndex}-${featureIndex}`,
          ...feature,
        });
      });
    }
    return features;
  }
  public seoFaq = [
    {
      question: 'Początki bywają trudne? U nas to proste!',
      answer: 'Tworzysz sesję, dodajesz zdjęcia, ustawiasz galerię i wysyłasz klientowi link. Pierwszą sesję przygotujesz w kilka minut.',
    },
    {
      question: 'Co mogę ustawić w galerii?',
      answer: 'Sam ustalasz zasady każdej sesji. Prosty kreator cennika pozwala ustawić ceny plików i odbitek, pakiety, dodatkowe ujęcia oraz budżet na odbitki. Do tego wybierzesz znak wodny, wiadomości, chat i pobieranie gotowych zdjęć.',
    },
    {
      question: 'Czy mogę porządkować sesje w folderach?',
      answer: 'Tak. Tworzysz własne foldery i układasz w nich sesje po swojemu — według klientów, terminów, rodzaju zlecenia albo czegokolwiek, co ułatwia Ci pracę.',
    },
    {
      question: 'Co dzieje się z moimi plikami RAW?',
      answer: 'RAWS konwertuje je lokalnie na Twoim urządzeniu. Oryginały RAW i dane EXIF nie trafiają na nasz serwer.',
    },
    {
      question: 'Czy mogę dodawać zdjęcia przez przeciąganie?',
      answer: 'Tak. Upuść pliki na kafelku nowej lub istniejącej sesji, aby dodać je do właściwej galerii.',
    },
    {
      question: 'Jakie skróty i gesty działają w sesji?',
      answer: 'Zaznaczaj zdjęcia ramką przeciąganą myszą albo klikaj z Shiftem, by wybrać cały zakres. Ctrl+A lub Cmd+A zaznacza wszystkie zdjęcia, a Escape czyści wybór.',
    },
    {
      question: 'Czy RAWS łączy się z Lightroomem?',
      answer: 'Tak. Połącz Lightroom z RAWS i dodawaj zdjęcia do galerii prosto z miejsca, w którym je obrabiasz.',
    },
    {
      question: 'Co robi klient po otrzymaniu linku?',
      answer: 'Otwiera galerię, wybiera ujęcia i składa zamówienie. Gdy udostępnisz gotowe pliki, od razu otrzyma e-mail z linkiem do ich pobrania.',
    },
  ];
  public faqOpenIndex: number | null = 0;
  private featureSliderAutoplayRaf: number | null = null;
  private featureSliderAutoplayPausedUntil = 0;
  private featureSliderDragging = false;
  private featureSliderLastFrameTime = 0;

  public toggleFaq(index: number) {
    this.faqOpenIndex = this.faqOpenIndex === index ? null : index;
  }

  private setupFeatureSlider() {
    const scroller = this.$refs.featureScroller as HTMLElement | undefined;
    if (!scroller) {
      return;
    }

    const mobileSlider = window.matchMedia('(max-width: 599px)');
    let nextMobileAdvanceAt = performance.now() + 5200;
    const getSingleLoopWidth = () => scroller.scrollWidth / 3;
    const normalizeLoopScroll = () => {
      const singleLoopWidth = getSingleLoopWidth();
      if (!Number.isFinite(singleLoopWidth) || singleLoopWidth <= 0) {
        return;
      }

      if (scroller.scrollLeft < singleLoopWidth * 0.5) {
        scroller.scrollLeft += singleLoopWidth;
      } else if (scroller.scrollLeft > singleLoopWidth * 1.5) {
        scroller.scrollLeft -= singleLoopWidth;
      }
    };
    const resetLoopPosition = () => {
      const singleLoopWidth = getSingleLoopWidth();
      if (!Number.isFinite(singleLoopWidth) || singleLoopWidth <= scroller.clientWidth) {
        return;
      }
      scroller.scrollLeft = singleLoopWidth;
    };
    let isMouseDragging = false;
    let dragStartX = 0;
    let dragStartScrollLeft = 0;
    let dragTravel = 0;

    const onMouseDown = (event: MouseEvent) => {
      if (event.button !== 0) {
        return;
      }
      isMouseDragging = true;
      this.featureSliderDragging = true;
      this.featureSliderAutoplayPausedUntil = performance.now() + 2400;
      dragStartX = event.clientX;
      dragStartScrollLeft = scroller.scrollLeft;
      dragTravel = 0;
      scroller.classList.add('is-dragging');
      document.body.classList.add('feature-slider-is-dragging');
      event.preventDefault();
    };

    const onMouseMove = (event: MouseEvent) => {
      if (!isMouseDragging) {
        return;
      }
      const delta = event.clientX - dragStartX;
      dragTravel = Math.max(dragTravel, Math.abs(delta));
      scroller.scrollLeft = dragStartScrollLeft - delta;
      event.preventDefault();
    };

    const endMouseDrag = () => {
      if (!isMouseDragging) {
        return;
      }
      isMouseDragging = false;
      this.featureSliderDragging = false;
      this.featureSliderAutoplayPausedUntil = performance.now() + 1600;
      nextMobileAdvanceAt = this.featureSliderAutoplayPausedUntil + 5200;
      scroller.classList.remove('is-dragging');
      document.body.classList.remove('feature-slider-is-dragging');
      if (dragTravel > 6) {
        normalizeLoopScroll();
      }
      dragTravel = 0;
    };

    const onMouseLeave = () => {
      endMouseDrag();
    };

    const releaseAutoplayLock = () => {
      isMouseDragging = false;
      this.featureSliderDragging = false;
      scroller.classList.remove('is-dragging');
      document.body.classList.remove('feature-slider-is-dragging');
    };

    const onScroll = () => {
      normalizeLoopScroll();
    };

    const onWheel = () => {
      this.featureSliderAutoplayPausedUntil = performance.now() + 1800;
      nextMobileAdvanceAt = this.featureSliderAutoplayPausedUntil + 5200;
    };

    const onTouchStart = () => {
      this.featureSliderAutoplayPausedUntil = performance.now() + 8000;
      nextMobileAdvanceAt = this.featureSliderAutoplayPausedUntil + 5200;
    };

    const startAutoplay = () => {
      const step = (now: number) => {
        if (!this.featureSliderLastFrameTime) {
          this.featureSliderLastFrameTime = now;
        }
        const deltaMs = Math.min(32, now - this.featureSliderLastFrameTime);
        this.featureSliderLastFrameTime = now;

        if (!this.featureSliderDragging && scroller.scrollWidth > scroller.clientWidth + 2 && now >= this.featureSliderAutoplayPausedUntil) {
          if (mobileSlider.matches) {
            if (now >= nextMobileAdvanceAt) {
              const firstCard = scroller.querySelector<HTMLElement>('.section-6__feature-card');
              if (firstCard) {
                const gap = parseFloat(getComputedStyle(scroller).gap) || 0;
                scroller.scrollBy({ left: firstCard.offsetWidth + gap, behavior: 'smooth' });
              }
              nextMobileAdvanceAt = now + 5200;
            }
          } else {
            scroller.scrollLeft += deltaMs * 0.072;
            normalizeLoopScroll();
          }
        }

        this.featureSliderAutoplayRaf = requestAnimationFrame(step);
      };

      if (this.featureSliderAutoplayRaf !== null) {
        cancelAnimationFrame(this.featureSliderAutoplayRaf);
      }
      this.featureSliderLastFrameTime = 0;
      this.featureSliderAutoplayRaf = requestAnimationFrame(step);
    };

    const onResize = () => {
      resetLoopPosition();
      nextMobileAdvanceAt = performance.now() + 5200;
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    scroller.addEventListener('mousedown', onMouseDown);
    scroller.addEventListener('mouseleave', onMouseLeave);
    scroller.addEventListener('wheel', onWheel, { passive: true });
    scroller.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', endMouseDrag);
    window.addEventListener('mouseleave', endMouseDrag);
    window.addEventListener('blur', releaseAutoplayLock);
    document.addEventListener('visibilitychange', releaseAutoplayLock);
    window.addEventListener('resize', onResize);
    window.addEventListener('load', onResize, { once: true });
    this.landingCleanup.push(() => scroller.removeEventListener('scroll', onScroll));
    this.landingCleanup.push(() => scroller.removeEventListener('mousedown', onMouseDown));
    this.landingCleanup.push(() => scroller.removeEventListener('mouseleave', onMouseLeave));
    this.landingCleanup.push(() => scroller.removeEventListener('wheel', onWheel));
    this.landingCleanup.push(() => scroller.removeEventListener('touchstart', onTouchStart));
    this.landingCleanup.push(() => window.removeEventListener('mousemove', onMouseMove));
    this.landingCleanup.push(() => window.removeEventListener('mouseup', endMouseDrag));
    this.landingCleanup.push(() => window.removeEventListener('mouseleave', endMouseDrag));
    this.landingCleanup.push(() => window.removeEventListener('blur', releaseAutoplayLock));
    this.landingCleanup.push(() => document.removeEventListener('visibilitychange', releaseAutoplayLock));
    this.landingCleanup.push(() => window.removeEventListener('resize', onResize));
    this.landingCleanup.push(() => window.removeEventListener('load', onResize));
    this.landingCleanup.push(() => {
      if (this.featureSliderAutoplayRaf !== null) {
        cancelAnimationFrame(this.featureSliderAutoplayRaf);
        this.featureSliderAutoplayRaf = null;
      }
    });

    this.$nextTick(() => {
      resetLoopPosition();
      requestAnimationFrame(resetLoopPosition);
      startAutoplay();
    });
  }

  private landingCleanup: Array<() => void> = [];

  mounted() {
    this.setupFeatureSlider();
  }

  beforeDestroy() {
    this.landingCleanup.forEach((cleanup) => cleanup());
  }
}
